import React, { useState, useEffect } from 'react';
import API from '../api';

const RemindersPanel = () => {
    const [activeTab, setActiveTab] = useState('reminders');
    const [reminderTemplate, setReminderTemplate] = useState('');
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [templateSaved, setTemplateSaved] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [channel, setChannel] = useState('SMS');
    const [savingChannel, setSavingChannel] = useState(false);
    const [contactPhone, setContactPhone] = useState('');
    const [savingContactPhone, setSavingContactPhone] = useState(false);
    const [contactPhoneSaved, setContactPhoneSaved] = useState(false);
    const [viberSmsFallback, setViberSmsFallback] = useState(false);
    const [savingFallback, setSavingFallback] = useState(false);

    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [services, setServices] = useState([]);
    const [patientQuery, setPatientQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [notificationText, setNotificationText] = useState('');
    const [sendingNotification, setSendingNotification] = useState(false);
    const [notificationResult, setNotificationResult] = useState(null);

    // Primaoci obaveštenja - mogu se dodavati iz sva tri izvora odjednom (pacijenti,
    // termini po datumu, CSV), spajaju se u jednu listu pre slanja.
    const [recipientMode, setRecipientMode] = useState('patients');
    const [recipients, setRecipients] = useState([]);

    const [bulkDate, setBulkDate] = useState('');
    const [dateFilterDoctorId, setDateFilterDoctorId] = useState('');
    const [dateFilterServiceId, setDateFilterServiceId] = useState('');
    const [dateAppointmentsRaw, setDateAppointmentsRaw] = useState([]);
    const [selectedDatePatientIds, setSelectedDatePatientIds] = useState(new Set());

    useEffect(() => {
        fetchChannel();
        API.get('/patients').then(res => setPatients(res.data)).catch(() => {});
        API.get('/doctors').then(res => setDoctors(res.data)).catch(() => {});
        API.get('/medical-services').then(res => setServices(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!bulkDate) {
            setDateAppointmentsRaw([]);
            return;
        }
        API.get('/appointments')
            .then(res => {
                const filtered = res.data.filter(a => a.status !== 'CANCELLED' && a.startTime.startsWith(bulkDate));
                setDateAppointmentsRaw(filtered);
                setSelectedDatePatientIds(new Set());
            })
            .catch(() => setDateAppointmentsRaw([]));
    }, [bulkDate]);

    useEffect(() => {
        setSelectedDatePatientIds(new Set());
    }, [dateFilterDoctorId, dateFilterServiceId]);

    // Filtrirano po doktoru/usluzi (npr. "ko ima kontrolu tog dana kod dr X"), pa
    // deduplikovano po pacijentu (jedna osoba se ne prikazuje dvaput u listi za dodavanje).
    const dateAppointments = (() => {
        const seen = new Set();
        return dateAppointmentsRaw.filter(a => {
            if (dateFilterDoctorId && String(a.doctorId) !== String(dateFilterDoctorId)) return false;
            if (dateFilterServiceId && String(a.serviceId) !== String(dateFilterServiceId)) return false;
            if (seen.has(a.patientId)) return false;
            seen.add(a.patientId);
            return true;
        });
    })();

    const fetchChannel = async () => {
        try {
            const res = await API.get('/messaging-settings');
            setChannel(res.data.channel);
            setContactPhone(res.data.contactPhone || '');
            setReminderTemplate(res.data.reminderTemplate || '');
            setViberSmsFallback(!!res.data.viberSmsFallback);
        } catch (err) {
            console.error("Greška pri učitavanju podešavanja kanala", err);
        }
    };

    const changeChannel = async (newChannel) => {
        setSavingChannel(true);
        try {
            await API.put('/messaging-settings', { channel: newChannel });
            setChannel(newChannel);
        } catch (err) {
            alert("Greška pri promeni kanala za slanje!");
        } finally {
            setSavingChannel(false);
        }
    };

    const toggleViberSmsFallback = async () => {
        const next = !viberSmsFallback;
        setSavingFallback(true);
        try {
            await API.put('/messaging-settings/viber-fallback', { viberSmsFallback: next });
            setViberSmsFallback(next);
        } catch (err) {
            alert("Greška pri promeni podešavanja fallback-a!");
        } finally {
            setSavingFallback(false);
        }
    };

    const saveContactPhone = async () => {
        setSavingContactPhone(true);
        try {
            await API.put('/messaging-settings/contact-phone', { contactPhone: contactPhone.trim() });
            setContactPhoneSaved(true);
            setTimeout(() => setContactPhoneSaved(false), 2000);
        } catch (err) {
            alert("Greška pri čuvanju kontakt telefona!");
        } finally {
            setSavingContactPhone(false);
        }
    };

    const sendPendingReminders = async () => {
        const channelLabel = channel === 'VIBER' ? 'Viber' : 'SMS';
        if (!window.confirm(`Poslati ${channelLabel} podsetnik svim pacijentima koji imaju zakazan pregled u narednih 24h, a još nije im poslat?`)) {
            return;
        }
        setSending(true);
        setSendResult(null);
        try {
            const res = await API.post('/reminders/send-now');
            setSendResult(`Poslato poruka: ${res.data.sent}`);
        } catch (err) {
            setSendResult("Greška pri slanju poruka!");
        } finally {
            setSending(false);
        }
    };

    const saveReminderTemplate = async () => {
        setSavingTemplate(true);
        try {
            await API.put('/messaging-settings/reminder-template', { reminderTemplate });
            setTemplateSaved(true);
            setTimeout(() => setTemplateSaved(false), 2000);
        } catch (err) {
            alert("Greška pri čuvanju poruke!");
        } finally {
            setSavingTemplate(false);
        }
    };

    const formatPatient = (p) => `${p.firstName} ${p.lastName} — ${p.phoneNumber}`;

    const patientSuggestions = patientQuery.trim().length > 0
        ? patients.filter(p => {
            const q = patientQuery.toLowerCase();
            const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
            const alreadyAdded = recipients.some(r => r.key === `p-${p.id}`);
            return !alreadyAdded && (fullName.includes(q) || (p.phoneNumber || '').includes(patientQuery));
        }).slice(0, 8)
        : [];

    const handlePatientQueryChange = (e) => {
        setPatientQuery(e.target.value);
        setShowSuggestions(true);
    };

    const addRecipients = (newOnes) => {
        setRecipients(prev => {
            const existingKeys = new Set(prev.map(r => r.key));
            const merged = [...prev];
            newOnes.forEach(r => {
                if (r.phone && !existingKeys.has(r.key)) {
                    merged.push(r);
                    existingKeys.add(r.key);
                }
            });
            return merged;
        });
    };

    const removeRecipient = (key) => {
        setRecipients(prev => prev.filter(r => r.key !== key));
    };

    const pickPatient = (patient) => {
        addRecipients([{ key: `p-${patient.id}`, patientId: patient.id, name: `${patient.firstName} ${patient.lastName}`, phone: patient.phoneNumber }]);
        setPatientQuery('');
        setShowSuggestions(false);
    };

    const toggleDatePatient = (patientId) => {
        setSelectedDatePatientIds(prev => {
            const next = new Set(prev);
            if (next.has(patientId)) next.delete(patientId); else next.add(patientId);
            return next;
        });
    };

    const selectAllDatePatients = () => {
        setSelectedDatePatientIds(new Set(dateAppointments.map(a => a.patientId)));
    };

    const addSelectedDateRecipients = () => {
        const chosen = dateAppointments
            .filter(a => selectedDatePatientIds.has(a.patientId))
            .map(a => ({ key: `p-${a.patientId}`, patientId: a.patientId, name: a.patientFullName, phone: a.patientPhone }));
        addRecipients(chosen);
        setSelectedDatePatientIds(new Set());
    };

    // Očekuje CSV sa dve kolone po redu: Ime,Telefon (zaglavlje je opciono - ako druga
    // kolona u prvom redu ne liči na broj telefona, taj red se preskače).
    const parseCsv = (text) => {
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const rows = lines.map(line => line.split(',').map(c => c.trim().replace(/^"|"$/g, '')));
        const looksLikeHeader = rows.length > 0 && !/\d{6,}/.test(rows[0][1] || '');
        const dataRows = looksLikeHeader ? rows.slice(1) : rows;
        return dataRows
            .filter(cols => cols.length >= 2 && cols[1])
            .map(cols => ({ key: `csv-${cols[1]}`, name: cols[0] || cols[1], phone: cols[1] }));
    };

    const handleCsvUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parsed = parseCsv(evt.target.result);
                if (parsed.length === 0) {
                    alert("Nije pronađen nijedan red sa imenom i telefonom u CSV fajlu.");
                    return;
                }
                addRecipients(parsed);
            } catch (err) {
                alert("Greška pri čitanju CSV fajla.");
            }
        };
        reader.readAsText(file, 'UTF-8');
        e.target.value = '';
    };

    const sendNotification = async () => {
        if (recipients.length === 0 || !notificationText.trim()) return;
        setSendingNotification(true);
        setNotificationResult(null);
        try {
            const res = await API.post('/notifications/send-bulk', {
                recipients: recipients.map(r => ({ patientId: r.patientId || null, name: r.name, phone: r.phone })),
                message: notificationText.trim()
            });
            setNotificationResult(`Poslato poruka: ${res.data.sent}`);
            setRecipients([]);
            setNotificationText('');
        } catch (err) {
            setNotificationResult(err.response?.data?.message || "Greška pri slanju poruke.");
        } finally {
            setSendingNotification(false);
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Poruke</h2>

            <div style={styles.tabBar}>
                <button
                    style={{ ...styles.tabBtn, ...(activeTab === 'reminders' ? styles.tabBtnActive : {}) }}
                    onClick={() => setActiveTab('reminders')}
                >
                    Podsetnici
                </button>
                <button
                    style={{ ...styles.tabBtn, ...(activeTab === 'notifications' ? styles.tabBtnActive : {}) }}
                    onClick={() => setActiveTab('notifications')}
                >
                    Obaveštenja
                </button>
            </div>

            <div style={styles.channelBox}>
                <span style={styles.channelLabel}>Kanal za slanje:</span>
                <div style={styles.channelToggle}>
                    <button
                        style={{ ...styles.channelBtn, ...(channel === 'SMS' ? styles.channelBtnActive : {}) }}
                        onClick={() => changeChannel('SMS')}
                        disabled={savingChannel || channel === 'SMS'}
                    >
                        SMS
                    </button>
                    <button
                        style={{ ...styles.channelBtn, ...(channel === 'VIBER' ? styles.channelBtnActive : {}) }}
                        onClick={() => changeChannel('VIBER')}
                        disabled={savingChannel || channel === 'VIBER'}
                    >
                        Viber
                    </button>
                </div>
                <span style={styles.hint}>Zajednički kanal za podsetnike i obaveštenja.</span>
            </div>

            {channel === 'VIBER' && (
                <label style={styles.fallbackBox}>
                    <input
                        type="checkbox"
                        checked={viberSmsFallback}
                        onChange={toggleViberSmsFallback}
                        disabled={savingFallback}
                    />
                    Ako pacijent nema Viber, automatski pošalji SMS umesto toga
                </label>
            )}

            {activeTab === 'reminders' && (
                <>
                    <div style={styles.contactPhoneBox}>
                        <label style={styles.channelLabel}>Kontakt telefon ordinacije:</label>
                        <input
                            type="text"
                            style={styles.contactPhoneInput}
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="npr. 011/123-456"
                        />
                        <button style={styles.saveBtn} onClick={saveContactPhone} disabled={savingContactPhone}>
                            {savingContactPhone ? 'Čuvanje...' : 'Sačuvaj'}
                        </button>
                        {contactPhoneSaved && <span style={styles.savedTag}>Sačuvano ✓</span>}
                    </div>
                    <p style={styles.hint}>
                        Ovaj broj se ubacuje u poruke podsetnika svuda gde se koristi placeholder <code>{'{kontakt}'}</code>.
                    </p>

                    <p style={styles.hint}>
                        Podsetnik — poruka koja se šalje automatski uz zakazan termin (podrazumevano, kao do sada),
                        ista za sve dane. Dostupni placeholderi: {' '}
                        <code>{'{pozdrav}'}</code> <code>{'{ime}'}</code> <code>{'{prezime}'}</code> <code>{'{datum}'}</code>{' '}
                        <code>{'{datumkratko}'}</code> <code>{'{dan}'}</code> <code>{'{vreme}'}</code> <code>{'{doktor}'}</code>{' '}
                        <code>{'{usluga}'}</code> <code>{'{kontakt}'}</code>
                    </p>
                    <p style={styles.hint}>
                        <code>{'{pozdrav}'}</code> automatski daje "Poštovani Stefane" ili "Poštovana Tina" u zavisnosti
                        od pola pacijenta (podešava se u listi pacijenata) — koristite ga umesto "Poštovani {'{ime}'}".
                        <code>{'{dan}'}</code> daje dan u nedelji za frazu "za ___" (npr. "za utorak", "za sredu").{' '}
                        <code>{'{datumkratko}'}</code> daje datum bez godine i vodećih nula (npr. "18.8."), za razliku
                        od <code>{'{datum}'}</code> koji daje pun format (18.08.2026.). <code>{'{doktor}'}</code> sada
                        daje puno ime i prezime doktora, a <code>{'{usluga}'}</code> ime usluge malim početnim slovom.
                    </p>
                    <p style={styles.hint}>
                        Podsetnici se automatski šalju: dan pre u 12h, dan pre u 21h (za naknadno zakazane termine)
                        i na sam dan u 8h ujutru (za termine zakazane u poslednji čas).
                    </p>

                    <div style={styles.manualSendBox}>
                        <button
                            style={{ ...styles.sendNowBtn, ...(sending ? styles.sendNowBtnDisabled : {}) }}
                            onClick={sendPendingReminders}
                            disabled={sending}
                        >
                            {sending ? 'Slanje u toku...' : `Pošalji sada (${channel === 'VIBER' ? 'Viber' : 'SMS'}) svima kojima nije poslato (u narednih 24h)`}
                        </button>
                        {sendResult && <span style={styles.sendResult}>{sendResult}</span>}
                    </div>

                    <div style={styles.card}>
                        <textarea
                            style={styles.textarea}
                            value={reminderTemplate}
                            onChange={(e) => setReminderTemplate(e.target.value)}
                        />
                        <div style={styles.actions}>
                            <button style={styles.saveBtn} onClick={saveReminderTemplate} disabled={savingTemplate}>
                                {savingTemplate ? 'Čuvanje...' : 'Sačuvaj'}
                            </button>
                            {templateSaved && <span style={styles.savedTag}>Sačuvano ✓</span>}
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'notifications' && (
                <>
                    <p style={styles.hint}>
                        Obaveštenje — slobodna poruka koju možete poslati kome i kad god poželite,
                        nevezano za zakazan termin. Dodajte primaoce iz jednog ili više izvora ispod, pa pošaljite svima odjednom.
                    </p>

                    <div style={styles.tabBar}>
                        <button
                            style={{ ...styles.subTabBtn, ...(recipientMode === 'patients' ? styles.subTabBtnActive : {}) }}
                            onClick={() => setRecipientMode('patients')}
                        >
                            Pacijenti
                        </button>
                        <button
                            style={{ ...styles.subTabBtn, ...(recipientMode === 'date' ? styles.subTabBtnActive : {}) }}
                            onClick={() => setRecipientMode('date')}
                        >
                            Po datumu termina
                        </button>
                        <button
                            style={{ ...styles.subTabBtn, ...(recipientMode === 'csv' ? styles.subTabBtnActive : {}) }}
                            onClick={() => setRecipientMode('csv')}
                        >
                            CSV
                        </button>
                    </div>

                    {recipientMode === 'patients' && (
                        <>
                            <label style={styles.label}>Dodaj pacijenta</label>
                            <div style={styles.autocompleteWrapper}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    placeholder="Ukucajte ime, prezime ili broj telefona..."
                                    value={patientQuery}
                                    onChange={handlePatientQueryChange}
                                    onFocus={() => setShowSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                />
                                {showSuggestions && patientSuggestions.length > 0 && (
                                    <div style={styles.suggestionsList}>
                                        {patientSuggestions.map(p => (
                                            <div key={p.id} style={styles.suggestionItem} onMouseDown={() => pickPatient(p)}>
                                                {formatPatient(p)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {showSuggestions && patientQuery.trim().length > 0 && patientSuggestions.length === 0 && (
                                    <div style={styles.suggestionsList}>
                                        <div style={{ ...styles.suggestionItem, cursor: 'default', color: '#94a3b8' }}>
                                            Nema pacijenata za "{patientQuery}"
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {recipientMode === 'date' && (
                        <>
                            <label style={styles.label}>Datum termina</label>
                            <input
                                type="date"
                                style={styles.input}
                                value={bulkDate}
                                onChange={(e) => setBulkDate(e.target.value)}
                            />

                            {bulkDate && (
                                <div style={styles.filterRow}>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.label}>Doktor</label>
                                        <select style={styles.input} value={dateFilterDoctorId} onChange={(e) => setDateFilterDoctorId(e.target.value)}>
                                            <option value="">Svi doktori</option>
                                            {doctors.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.label}>Tip pregleda</label>
                                        <select style={styles.input} value={dateFilterServiceId} onChange={(e) => setDateFilterServiceId(e.target.value)}>
                                            <option value="">Sve usluge</option>
                                            {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {bulkDate && (
                                <>
                                    {dateAppointments.length === 0 ? (
                                        <div style={{ ...styles.hint, marginBottom: '15px' }}>
                                            {dateFilterDoctorId || dateFilterServiceId
                                                ? 'Nema termina za taj datum sa izabranim filterima.'
                                                : 'Nema zakazanih termina za taj datum.'}
                                        </div>
                                    ) : (
                                        <>
                                            <div style={styles.dateListActions}>
                                                <button style={styles.smallBtn} onClick={selectAllDatePatients}>Izaberi sve</button>
                                                <button style={styles.smallBtn} onClick={() => setSelectedDatePatientIds(new Set())}>Poništi</button>
                                                <button
                                                    style={{ ...styles.smallBtnPrimary, opacity: selectedDatePatientIds.size === 0 ? 0.6 : 1 }}
                                                    onClick={addSelectedDateRecipients}
                                                    disabled={selectedDatePatientIds.size === 0}
                                                >
                                                    Dodaj izabrane ({selectedDatePatientIds.size})
                                                </button>
                                            </div>
                                            <div style={styles.dateList}>
                                                {dateAppointments.map(a => (
                                                    <label key={a.patientId} style={styles.dateListItem}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDatePatientIds.has(a.patientId)}
                                                            onChange={() => toggleDatePatient(a.patientId)}
                                                        />
                                                        <span>{a.patientFullName} <span style={styles.phone}>({a.patientPhone})</span> — {a.serviceName} u {new Date(a.startTime).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {recipientMode === 'csv' && (
                        <>
                            <label style={styles.label}>CSV fajl (kolone: Ime, Telefon)</label>
                            <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ marginBottom: '20px' }} />
                            <p style={styles.hint}>
                                Jedan red po osobi: ime i broj telefona razdvojeni zarezom (npr. <code>Marko Marković,0641234567</code>).
                                Zaglavlje (naslov kolona) je opciono.
                            </p>
                        </>
                    )}

                    {recipients.length > 0 && (
                        <>
                            <label style={styles.label}>Izabrani primaoci ({recipients.length})</label>
                            <div style={styles.dateList}>
                                {recipients.map(r => (
                                    <div key={r.key} style={styles.recipientRow}>
                                        <span>{r.name} <span style={styles.phone}>({r.phone})</span></span>
                                        <button style={styles.removeChipBtn} onClick={() => removeRecipient(r.key)}>✕</button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <label style={styles.label}>Poruka</label>
                    <textarea
                        style={styles.textarea}
                        value={notificationText}
                        onChange={(e) => setNotificationText(e.target.value)}
                        placeholder="Tekst obaveštenja..."
                    />

                    <div style={styles.manualSendBox}>
                        <button
                            style={{ ...styles.sendNowBtn, opacity: (recipients.length === 0 || !notificationText.trim() || sendingNotification) ? 0.6 : 1 }}
                            onClick={sendNotification}
                            disabled={recipients.length === 0 || !notificationText.trim() || sendingNotification}
                        >
                            {sendingNotification ? 'Slanje...' : `Pošalji (${channel === 'VIBER' ? 'Viber' : 'SMS'}) — ${recipients.length} ${recipients.length === 1 ? 'primalac' : 'primalaca'}`}
                        </button>
                        {notificationResult && <span style={styles.sendResult}>{notificationResult}</span>}
                    </div>
                </>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    title: { margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' },
    hint: { fontSize: '13px', color: '#7f8c8d', marginBottom: '10px', lineHeight: '1.5' },
    tabBar: { display: 'flex', gap: '6px', marginBottom: '15px', borderBottom: '1px solid #e2e8f0' },
    tabBtn: {
        padding: '10px 16px', backgroundColor: 'transparent', color: '#64748b',
        border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
    },
    tabBtnActive: { color: '#1c8aa6', borderBottom: '2px solid #1c8aa6' },
    subTabBtn: {
        padding: '7px 14px', backgroundColor: '#f1f5f9', color: '#475569',
        border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
    },
    subTabBtnActive: { backgroundColor: '#1c8aa6', color: 'white', border: '1px solid #1c8aa6' },
    filterRow: { display: 'flex', gap: '15px' },
    label: { display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569', fontSize: '14px' },
    phone: { fontWeight: 'normal', color: '#7f8c8d', fontSize: '13px' },
    dateListActions: { display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
    smallBtn: {
        padding: '6px 12px', backgroundColor: '#f1f5f9', color: '#475569',
        border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
    },
    smallBtnPrimary: {
        padding: '6px 12px', backgroundColor: '#1c8aa6', color: 'white',
        border: '1px solid #1c8aa6', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
    },
    dateList: {
        backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: '20px', maxHeight: '260px', overflowY: 'auto'
    },
    dateListItem: {
        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 15px',
        borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', cursor: 'pointer'
    },
    recipientRow: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 15px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b'
    },
    removeChipBtn: {
        background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '2px 6px'
    },
    input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '15px', outline: 'none', boxSizing: 'border-box', marginBottom: '20px' },
    autocompleteWrapper: { position: 'relative' },
    suggestionsList: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        zIndex: 10,
        maxHeight: '220px',
        overflowY: 'auto'
    },
    suggestionItem: {
        padding: '10px 12px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#1e293b',
        borderBottom: '1px solid #f1f5f9'
    },
    channelBox: { display: 'flex', alignItems: 'center', gap: '12px', margin: '15px 0', flexWrap: 'wrap' },
    channelLabel: { fontSize: '14px', fontWeight: '600', color: '#1f2937' },
    channelToggle: { display: 'flex', gap: '6px' },
    channelBtn: {
        padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#475569',
        border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
    },
    channelBtnActive: { backgroundColor: '#1c8aa6', color: 'white', border: '1px solid #1c8aa6' },
    contactPhoneBox: { display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0', flexWrap: 'wrap' },
    fallbackBox: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 15px 0', fontSize: '13px', color: '#475569', cursor: 'pointer' },
    contactPhoneInput: { padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', minWidth: '180px' },
    manualSendBox: { display: 'flex', alignItems: 'center', gap: '15px', margin: '15px 0' },
    sendNowBtn: { padding: '10px 16px', backgroundColor: '#ec4899', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    sendNowBtnDisabled: { backgroundColor: '#f9a8d4', cursor: 'not-allowed' },
    sendResult: { fontSize: '13px', color: '#1f2937', fontWeight: '600' },
    list: { display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' },
    card: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    },
    dayLabel: { fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' },
    textarea: {
        width: '100%',
        minHeight: '70px',
        padding: '10px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        fontSize: '14px',
        boxSizing: 'border-box',
        resize: 'vertical'
    },
    actions: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' },
    saveBtn: { padding: '8px 14px', backgroundColor: '#2980b9', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    savedTag: { fontSize: '13px', color: '#10b981', fontWeight: '600' }
};

export default RemindersPanel;
